import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DESTINATION_OPTIONS,
  TEAMS_BOT_DEFAULTS,
} from '@elementary/common-ui/constants/queryKeys';
import {
  FlexColumn,
  LoadingButton,
  TextField,
} from '@elementary/common-ui/components';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import useSnackbar from 'shared/hooks/useSnackbar';
import useAlertDestinations from 'shared/hooks/useAlertDestinations';
import * as api from 'shared/services/settingsApi';

type Props = {
  envId: string;
};

const TeamsBotDefaultChannelSection = ({ envId }: Props) => {
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { alertDestinationOptions } = useAlertDestinations();
  const channels = useMemo(
    () => alertDestinationOptions?.microsoft_teams_bot?.options ?? [],
    [alertDestinationOptions],
  );

  const defaultsQuery = useQuery([TEAMS_BOT_DEFAULTS, envId], () =>
    api.fetchTeamsBotDefaults(envId),
  );
  const existingChannel = defaultsQuery.data?.defaultDestination?.channel ?? '';

  const [selectedChannel, setSelectedChannel] = useState<string>('');
  useEffect(() => {
    setSelectedChannel(existingChannel);
  }, [existingChannel]);

  const saveMutation = useMutation(
    async () =>
      existingChannel
        ? api.updateTeamsBotDefaults(selectedChannel, envId)
        : api.setTeamsBotDefaults(selectedChannel, envId),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([TEAMS_BOT_DEFAULTS, envId]);
        void queryClient.invalidateQueries([DESTINATION_OPTIONS]);
        openSnackbar({
          message: 'Default Teams channel saved',
          variant: 'success',
        });
      },
      onError: (error: MutationError) => {
        openSnackbar({
          message:
            error?.response?.data?.message ||
            error?.message ||
            'Failed to save the default Teams channel',
          variant: 'error',
        });
      },
    },
  );

  const selectedOption = useMemo(
    () => channels.find((channel) => channel.value === selectedChannel) || null,
    [channels, selectedChannel],
  );

  const canSave =
    !!selectedChannel &&
    selectedChannel !== existingChannel &&
    !saveMutation.isLoading;

  return (
    <FlexColumn sx={{ gap: 2 }}>
      <Typography>
        Pick a default channel to start receiving alerts right away. This adds a
        default alert rule that sends all failures there — refine it anytime
        from the Alert Rules page.
      </Typography>
      <Autocomplete
        options={channels}
        value={selectedOption}
        onChange={(_, newValue) => setSelectedChannel(newValue?.value || '')}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        disabled={saveMutation.isLoading || defaultsQuery.isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Default channel"
            placeholder="e.g., Team / General"
            fullWidth
            size="large"
          />
        )}
      />
      <LoadingButton
        onClick={() => saveMutation.mutate()}
        disabled={!canSave}
        loading={saveMutation.isLoading}
        sx={{ alignSelf: 'flex-start' }}
      >
        Save default channel
      </LoadingButton>
    </FlexColumn>
  );
};

export default TeamsBotDefaultChannelSection;
